import { z } from 'zod'
import { type LegalSourceCitationBehavior } from '#app/services/legal-ai/shared/source-contract.ts'
import { PANAMA_CATALOG_SOURCE_TYPES, getPanamaCatalogSource } from './catalog-sources.ts'

export const PANAMA_COLLECTION = 'panama_legal_documents'
export const PANAMA_INDEX_VERSION = 2
export const PANAMA_INDEX_STATE_VERSION = 1
export const PANAMA_SUPPORTED_INDEX_VERSIONS = [1, PANAMA_INDEX_VERSION] as const
export const PANAMA_SOURCE_TYPES = [
	'infojuridica',
	'infojuridica-constitutional',
	'legispan-norms',
	'gaceta-documents',
	'legispan-gazettes',
	'legispan-records',
	'organo-judicial',
	'cendoj-documents',
	'jurisis',
	'bafa',
	'tribunal-electoral-bulletins',
	'canal-register',
	'canal-regulations',
	'amp-normativity',
	'mire-treaties',
	'tacp-resolutions',
	'tat-resolutions',
	'tafp-decisions',
	'assembly-bills',
	'panamacompra-compendium',
	'procuraduria-opinions',
	'dgi-consultations',
	'css-publications',
	...PANAMA_CATALOG_SOURCE_TYPES,
] as const

export const panamaRecordSchema = z.object({
	idDocument: z.string().startsWith('pa-'),
	country: z.literal('PA'),
	sourceType: z.enum(PANAMA_SOURCE_TYPES),
	source: z.string(),
	legalKind: z.string(),
	documentType: z.string(),
	title: z.string(),
	number: z.string(),
	url: z.string().url(),
	content: z.string().trim().min(1),
	year: z.number().nullable().optional(),
	publishedAt: z.string().nullable().optional(),
	date: z.string().nullable().optional(),
	authority: z.string().optional(),
	proceduralStage: z.string().optional(),
	proponents: z.string().optional(),
	projectNumber: z.string().optional(),
	anteprojectNumber: z.string().optional(),
	gazetteNumber: z.string().optional(),
	textScope: z.string().optional(),
	processingWarning: z.string().optional(),
	territorialScope: z.object({ level: z.enum(['national', 'municipal']), locality: z.string().optional() }).optional(),
	catalogMetadata: z.object({
		catalogUrls: z.array(z.string().url()), description: z.string(), status: z.string().optional(),
		annexUrls: z.array(z.string().url()), documentDateRaw: z.string().optional(),
		annexTextComplete: z.boolean().optional(), primaryTextLength: z.number().int().nonnegative().optional(),
		identityConflict: z.boolean().optional(),
		primaryRecoveryUrls: z.array(z.string().url()).optional(),
	}).optional(),
	legalMetadata: z
		.object({
			vigency: z.string().optional(),
			gazettes: z.string().optional(),
			comment: z.string().optional(),
			matters: z.array(z.string()).optional(),
			vigencyHistory: z.array(z.array(z.string())).optional(),
			constitutionalCaseLaw: z.array(z.array(z.string())).optional(),
			references: z.array(z.array(z.string())).optional(),
		})
		.optional(),
	caseMetadata: z
		.object({
			institution: z.string().optional(),
			chambers: z.array(z.string()).optional(),
			issuers: z.array(z.string()).optional(),
			reportingJudges: z.array(z.string()).optional(),
			judges: z.array(z.string()).optional(),
			presidingJudges: z.array(z.string()).optional(),
			outcomes: z.array(z.string()).optional(),
			docketNumbers: z.array(z.string()).optional(),
			businessNumbers: z.array(z.string()).optional(),
			decisionDates: z.array(z.string()).optional(),
			businessDates: z.array(z.string()).optional(),
			enforceabilityDates: z.array(z.string()).optional(),
			sentenceDates: z.array(z.string()).optional(),
			thematicCategories: z.array(z.string()).optional(),
			branchesOfLaw: z.array(z.string()).optional(),
			documentClassifications: z.array(z.string()).optional(),
			processTypes: z.array(z.string()).optional(),
			instances: z.array(z.string()).optional(),
			judicialOffices: z.array(z.string()).optional(),
			judicialDistricts: z.array(z.string()).optional(),
			gazettePublication: z.string().optional(),
			categories: z.array(z.string()).optional(),
			tags: z.array(z.string()).optional(),
			province: z.string().optional(),
			judicialSystem: z.string().optional(),
			affectedInstitution: z.string().optional(),
			naturalPersons: z.string().optional(),
			legalPersons: z.string().optional(),
			officialDescription: z.string().optional(),
			officialCollection: z.string().optional(),
		})
		.optional(),
	documentUrls: z.record(z.string()).optional(),
})

export type PanamaRecord = z.infer<typeof panamaRecordSchema>
export type PanamaPayload = PanamaRecord & {
	chunk_index: number
	chunk_count: number
	index_version: number
}

// A subject heading such as “Constitución y Justicia Constitucional” does not
// identify a constitution. Keep historical constitutions without implying current force.
export function isPanamaConstitutionTitle(title: string): boolean {
	return /^constituci[oó]n(?:\s+pol[ií]tica)?(?:\s+(?:de\b|\d{4}\b)|$)/iu.test(title.trim())
}

export function hasSubstantivePanamaLegalText(value: unknown): value is string {
	if (typeof value !== 'string' || !value.trim()) return false
	// LEGISPAN PDFs can expose a searchable catalogue cover while the attached
	// law is scanned. The cover and digitization notice cannot stand in for its body.
	if (/LEGISPAN[\s\S]{0,150}Tipo de Norma:/iu.test(value.slice(0, 500))) {
		const coverEnd = /Rollo\s*:\s*\d+\s+Posici[oó]n\s*:\s*\d+/iu.exec(value)
		if (coverEnd) {
			const body = value
				.slice(coverEnd.index + coverEnd[0].length)
				.replace(
					/ACUERDO DE COOPERACI[OÓ]N\s+ENTRE\s+LA ASAMBLEA NACIONAL\s+Y\s+EL MINISTERIO DE RELACIONES EXTERIORES\s+2006\s+Para contribuir[\s\S]{0,500}?procesador de palabras\./iu,
					'',
				)
				.replace(/Digitalizado por la Asamblea Nacional/giu, '')
				.replace(/[\s\f]/g, '')
			if (body.length < 200) return false
		}
	}
	// Some PDFs expose glyph positions as spaces inside almost every word. Such
	// text is nonempty but cannot support embeddings or exact article recovery.
	// Require both repeated fragmented headings and pervasive single letters so
	// decorative headings or ordinary Spanish one-letter words remain valid.
	const sample = value.slice(0, 40_000)
	// Older municipal gazettes concatenate page numbers and omit the dot in No.
	// Their long contents cover must not make a header-only scanned body reusable.
	const gazetteHeader =
		/^[\t ]*\d*No\.?[\t ]*\d+[A-Z -]*[\t ]*Gaceta Oficial Digital,[\t ]*\p{L}+[\t ]+\d{1,2}[\t ]+de[\t ]+\p{L}+[\t ]+de[\t ]+\d{4,}[\t ]*\d*[\t ]*$/gimu
	const contents = /^[\t ]*CONTENIDO[\t ]*$/imu.exec(sample)
	if (contents) {
		// The first page may itself have a header before the contents inventory.
		// Start at the following page so the inventory cannot count as legal text.
		gazetteHeader.lastIndex = contents.index + contents[0].length
		const bodyHeader = gazetteHeader.exec(sample)
		if (bodyHeader) {
			const body = value.slice(bodyHeader.index)
			gazetteHeader.lastIndex = 0
			// Readable notices and a multi-page contents inventory can conceal an
			// image-only legal section. A sustained run of empty page bodies
			// requires OCR even when another part of the issue has searchable text.
			if (
				/CORTE\s+SUPREMA[\s\S]*Fallo\s+N/iu.test(sample) ||
				(/CON[CS]EJO\s+MUNICIPAL[\s\S]*Acuerdo\s+(?:Municipal\s+)?N/iu.test(sample) &&
					/www\.gacetaoficial\.gob\.pa\/validar-gaceta/iu.test(body))
			) {
				const headers = [...body.matchAll(gazetteHeader)]
				let emptyPages = 0
				for (let index = 1; index < headers.length; index++) {
					const previous = headers[index - 1]!
					// Authentication footers are selectable even when the act is scanned.
					const page = body.slice(previous.index! + previous[0].length, headers[index]!.index)
						.replace(/Gaceta Oficial Digital\s+Para verificar la autenticidad[^\n]*\n\s*en el sitio web www\.gacetaoficial\.gob\.pa\/validar-gaceta/giu, '')
						.replace(/^GO[A-F0-9]+[\t ]*$/gimu, '')
					emptyPages = page.trim() ? 0 : emptyPages + 1
					if (emptyPages >= 8) return false
				}
			}
			if (
				[...body.matchAll(gazetteHeader)].length >= 8 &&
				!body
					.replace(gazetteHeader, '')
					.replace(/^Powered by TCPDF \(www\.tcpdf\.org\)[\t ]*$/gimu, '')
					.trim()
			)
				return false
		}
	}
	const articleWord = /\bA\s*r\s*t\s*[ií]\s*c\s*u\s*l\s*o\b/giu
	const fragmentedHeadings = [...sample.matchAll(articleWord)].filter(match => /\s/.test(match[0]))
	if (fragmentedHeadings.length >= 3) {
		const body = sample.replace(articleWord, ' ')
		const words = body.match(/\p{L}+/gu) ?? []
		if (words.length >= 200 && words.filter(word => word.length === 1).length / words.length > 0.4) return false
	}
	// Mixed gazettes can expose a legislative cover and readable notices while
	// almost every law page is an image. Nonempty notices cannot certify the law.
	if (
		/GACETA\s+OFICIAL[\s\S]{0,1500}CONTENIDO[\s\S]{0,500}ASAMBLEA\s+NACIONAL[\s\S]{0,300}LEY\s+N[°ºo.\s]*\d/iu.test(
			sample,
		)
	) {
		const pages = sample.split('\f')
		const emptyLawPages = pages.filter(
			page =>
				page
					.replace(/^No\.[^\n]*Gaceta Oficial Digital[^\n]*$/gimu, '')
					.replace(
						/Gaceta Oficial Digital\s+Para verificar la autenticidad[^\n]*\n\s*en el sitio web www\.gacetaoficial\.gob\.pa\/validar-gaceta/giu,
						'',
					)
					.trim().length < 80,
		)
		if (
			(pages.length >= 8 && emptyLawPages.length / pages.length > 0.8) ||
			/www\.gacetaoficial\.gob\.pa\/validar-gaceta\s+AVISOS\b/iu.test(
				sample.replace(/^No\.[^\n]*Gaceta Oficial Digital[^\n]*$/gimu, ''),
			)
		)
			return false
	}
	// Poppler preserves page breaks even when the repository's digitization
	// stamp splits a gazette header across lines. Readable acts on other pages
	// cannot certify a sustained image-only section of the same issue.
	if (value.includes('\f') && /Gaceta Oficial Digital/i.test(sample)) {
		let emptyPages = 0
		for (const page of value.split('\f')) {
			const body = page
				.replace(/Digitalizado por la Asamblea Nacional/giu, '')
				.replace(/No\.?\s*\d{4,}[A-Z -]*\s*Gaceta Oficial Digital,[^\n]*/giu, '')
				.replace(/Gaceta Oficial Digital\s+Para verificar la autenticidad[^\n]*\n\s*en el sitio web www\.gacetaoficial\.gob\.pa\/validar-gaceta/giu, '')
				.replace(/^GO[A-F0-9]+[\t ]*$/gimu, '')
				.replace(/Powered by TCPDF \(www\.tcpdf\.org\)/giu, '')
				.trim()
			emptyPages = body.length < 80 ? emptyPages + 1 : 0
			if (emptyPages >= 8) return false
		}
	}
	const withoutKnownPlaceholders = value
		.replace(/DOCUMENTO NO EXISTE[.!]?/gi, '')
		.replace(/Digitalizado por la Asamblea Nacional/gi, '')
		.replace(/\f/g, '')
		.trim()
	if (!withoutKnownPlaceholders) return false
	if (!withoutKnownPlaceholders.includes('Gaceta Oficial Digital')) return true
	const substantive = withoutKnownPlaceholders
		.split('\n')
		.map(line =>
			line
				.replace(/Gaceta Oficial Digital/gi, '')
				.replace(/Para verificar la autenticidad/gi, '')
				.replace(/en el sitio web www\.gacetaoficial\.gob\.pa/gi, '')
				.replace(/Powered by TCPDF(?: \(www\.tcpdf\.org\))?/gi, '')
				.replace(/\s+/g, ' ')
				.trim(),
		)
		.filter(
			line =>
				line &&
				!line.startsWith('DOCUMENTO PUBLICADO') &&
				!/^GO[A-F0-9]+$/i.test(line) &&
				!/^No\.?\s*\d+[A-Z -]*,?\s+[^\n]{0,100}\s+\d+$/i.test(line),
		)
		.join(' ')
	return substantive.length >= 200
}

/** Reject explicit publication identity conflicts, not absent or uncertain OCR headers. */
export function panamaRecordIdentityConflict(value: unknown): string | null {
	if (!value || typeof value !== 'object') return null
	const record = value as { sourceType?: unknown; textContent?: unknown; gazetteNumber?: unknown }
	if (record.sourceType !== 'legispan-norms' || typeof record.textContent !== 'string') return null
	const normalize = (number: string) => number.toUpperCase().replace(/[\s-]/g, '')
	const expected = typeof record.gazetteNumber === 'string' ? normalize(record.gazetteNumber) : ''
	// Restrict this check to an explicit opening publication header. References to
	// earlier gazettes inside a law do not identify the attachment itself.
	const opening = record.textContent.match(/^\s*G\.\s*O\.\s*(\d{4,6}(?:[ -]?[A-Z])?)[ \t]*(?:\r?\n|$)/i)?.[1]
	if (!/^\d{4,6}[A-Z]?$/.test(expected) || !opening || normalize(opening) === expected) return null
	return `Publication identity conflict: catalogue gazette ${expected}, attachment opening gazette ${normalize(opening)}`
}

export function hasSubstantivePanamaRecordText(value: unknown): boolean {
	if (!value || typeof value !== 'object') return false
	const record = value as { sourceType?: unknown; textContent?: unknown }
	if (!hasSubstantivePanamaLegalText(record.textContent) || panamaRecordIdentityConflict(value)) return false
	// A Gazette issue necessarily contains publication furniture plus at least one
	// legal notice. Shorter LEGISPAN OCR results observed in the historic catalog
	// are isolated names or scan artifacts, never the complete issue.
	return record.sourceType !== 'legispan-gazettes' || record.textContent.trim().length >= 200
}

const citation: LegalSourceCitationBehavior<any> = {
	title: payload => [payload.documentType, payload.number, payload.title].filter(Boolean).join(' — '),
	url: payload => payload.url || '',
	inlineTagName: 'Panama',
	iconName: 'file-text',
}

// These sources have different evidentiary roles. Infojurídica records vigency,
// Jurisis is a curated excerpt, and legislative records are history rather than enacted law.
export const panamaSourceDefinitions = PANAMA_SOURCE_TYPES.map(sourceType => {
	const label = getPanamaCatalogSource(sourceType)?.label ?? ({
		infojuridica: 'Infojurídica — Normativa, vigencia y relaciones',
		'infojuridica-constitutional': 'Infojurídica — Jurisprudencia constitucional',
		'legispan-norms': 'LEGISPAN — Normativa',
		'gaceta-documents': 'Gaceta Oficial — Documentos publicados',
		'legispan-gazettes': 'Gaceta Oficial — Ediciones completas',
		'legispan-records': 'LEGISPAN — Actas legislativas',
		'organo-judicial': 'Órgano Judicial — Consulta de Fallos',
		'cendoj-documents': 'Órgano Judicial — CENDOJ legislación y códigos',
		jurisis: 'Jurisis — Jurisprudencia sistematizada de la Sala Tercera',
		bafa: 'BAFA — Fallos de sentencias',
		'tribunal-electoral-bulletins': 'Tribunal Electoral — Boletines oficiales',
		'canal-register': 'Autoridad del Canal — Registro del Canal',
		'canal-regulations': 'Autoridad del Canal — Reglamentos y acuerdos',
		'amp-normativity': 'Autoridad Marítima — Normativa y circulares',
		'mire-treaties': 'Ministerio de Relaciones Exteriores — Tratados',
		'tacp-resolutions': 'Tribunal de Contrataciones Públicas — Resoluciones',
		'tat-resolutions': 'Tribunal Administrativo Tributario — Resoluciones',
		'tafp-decisions': 'Tribunal de la Función Pública — Decisiones y acuerdos',
		'assembly-bills': 'Asamblea Nacional — Proyectos y anteproyectos',
		'panamacompra-compendium': 'PanamaCompra — Consultas y pronunciamientos',
		'procuraduria-opinions': 'Procuraduría — Vistas, consultas, opiniones y circulares',
		'dgi-consultations': 'Dirección General de Ingresos — Consultas tributarias',
		'css-publications': 'Caja de Seguro Social — Comunicados oficiales',
	} as Record<string, string>)[sourceType]!
	return {
		id: `pa/${sourceType}`,
		country: 'PA' as const,
		sourceType,
		label,
		citationType: `Panama-${sourceType}`,
		citation: { ...citation, inlineTagName: `panama-${sourceType}`, summaryLabel: label },
	}
})

const infojuridicaDetailMetadataSchema = z.object({
	general: z.record(z.string()).optional(),
	matters: z.array(z.string()).optional(),
	vigencyRows: z.array(z.array(z.string())).optional(),
	constitutionalCaseLaw: z.array(z.array(z.string())).optional(),
	references: z.array(z.array(z.string())).optional(),
})

export function infojuridicaLegalMetadata(detail: unknown) {
	const parsed = infojuridicaDetailMetadataSchema.safeParse(detail)
	if (!parsed.success) return undefined
	const { general = {}, matters, vigencyRows, constitutionalCaseLaw, references } = parsed.data
	return {
		vigency: general.Vigencia,
		gazettes: general.Gacetas,
		comment: general.Comentario,
		matters,
		vigencyHistory: vigencyRows,
		constitutionalCaseLaw,
		references,
	}
}

function nonEmptyStrings(value: unknown) {
	const values = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value]
	const normalized = values
		.filter((item): item is string | number => typeof item === 'string' || typeof item === 'number')
		.map(String)
		.map(item => item.trim())
		.filter(Boolean)
	return normalized.length ? normalized : undefined
}

function nonEmptyString(value: unknown) {
	return nonEmptyStrings(value)?.[0]
}

function compactMetadata<T extends Record<string, unknown>>(value: T) {
	const compacted = Object.fromEntries(
		Object.entries(value).filter(([, item]) => item !== undefined && (!Array.isArray(item) || item.length > 0)),
	) as T
	return Object.keys(compacted).length ? compacted : undefined
}

export function panamaCaseMetadata(record: Record<string, unknown>) {
	const metadata =
		record.metadata && typeof record.metadata === 'object' ? (record.metadata as Record<string, unknown>) : {}
	if (record.sourceType === 'infojuridica-constitutional') {
		return compactMetadata({
			institution: nonEmptyString(metadata.institution),
			chambers: nonEmptyStrings(metadata.chamber),
			reportingJudges: nonEmptyStrings(metadata.magistrate),
			processTypes: nonEmptyStrings(metadata.process),
			outcomes: nonEmptyStrings(metadata.decision),
			gazettePublication: nonEmptyString(metadata.publishedInGazette),
		})
	}
	if (record.sourceType === 'organo-judicial') {
		const properties =
			metadata.properties && typeof metadata.properties === 'object'
				? (metadata.properties as Record<string, unknown>)
				: {}
		return compactMetadata({
			issuers: nonEmptyStrings(properties.Emisor),
			reportingJudges: nonEmptyStrings(properties.Ponente),
			judges: nonEmptyStrings(properties.Juez),
			presidingJudges: nonEmptyStrings(properties['Presidente del tribunal']),
			outcomes: nonEmptyStrings(properties['Sentido del fallo']),
			docketNumbers: nonEmptyStrings(properties['Número de expediente']),
			businessNumbers: nonEmptyStrings(properties['Número de negocio']),
			decisionDates: nonEmptyStrings(properties['Fecha de la decisión']),
			businessDates: nonEmptyStrings(properties['Fecha de Negocio']),
			enforceabilityDates: nonEmptyStrings(properties['Fecha de ejecutoría']),
			sentenceDates: nonEmptyStrings(properties['Fecha de sentencia']),
			thematicCategories: nonEmptyStrings(properties['Categoría Temática']),
			branchesOfLaw: nonEmptyStrings(properties['Rama del Derecho']),
			documentClassifications: nonEmptyStrings(properties['Tipo de documento']),
			processTypes: nonEmptyStrings(properties['Tipo de proceso']),
			instances: nonEmptyStrings(properties.Instancia),
			judicialOffices: nonEmptyStrings(properties['Dependencia Judicial']),
			judicialDistricts: nonEmptyStrings(properties['Distrito Judicial']),
		})
	}
	if (record.sourceType === 'jurisis') {
		return compactMetadata({
			categories: nonEmptyStrings(record.categories),
			tags: nonEmptyStrings(record.tags),
			officialCollection: 'Jurisprudencia sistematizada de la Sala Tercera',
		})
	}
	if (record.sourceType === 'bafa') {
		return compactMetadata({
			province: nonEmptyString(record.province),
			judicialSystem: nonEmptyString(record.system),
			processTypes: nonEmptyStrings(record.procedureType),
			affectedInstitution: nonEmptyString(record.affectedInstitution),
			naturalPersons: nonEmptyString(record.naturalPersons),
			legalPersons: nonEmptyString(record.legalPersons),
			officialDescription: nonEmptyString(record.description),
		})
	}
	return undefined
}

export function inferPanamaTextScope(record: {
	sourceType?: string
	textScope?: string
	documentUrls?: Record<string, string>
	content?: string
	textContent?: string
}) {
	if (record.textScope === 'official_catalog_metadata') return record.textScope
	if (record.sourceType !== 'infojuridica') {
		// Any official repository can host a whole gazette under a norm-named PDF.
		// Classify the actual publication, independently of the source registration.
		// Require a gazette masthead,
		// contents heading and multiple acts or an act plus notices; page headers alone
		// also occur on correctly extracted individual statutes.
		const text =
			typeof record.textContent === 'string' && record.textContent
				? record.textContent
				: typeof record.content === 'string'
					? record.content
					: ''
		const opening = text
			.slice(0, 8_000)
			.normalize('NFD')
			.replace(/\p{Diacritic}/gu, '')
			.toUpperCase()
		const contents = /\bCONTENIDO\b/.exec(opening)
		if (contents && contents.index < 3_000 && /GACETA\s+OFICIAL/.test(opening.slice(0, contents.index))) {
			const tail = opening.slice(contents.index + contents[0].length)
			const nextHeader = tail.search(/GACETA\s+OFICIAL/)
			const inventory = tail.slice(0, nextHeader < 0 ? 4_000 : nextHeader)
			const acts = [
				...inventory.matchAll(
					/^[ \t]*(LEY|DECRETO(?:[ \t]+(?:EJECUTIVO|LEY|DE[ \t]+GABINETE))?|RESOLUCION(?:[ \t]+(?:DE[ \t]+GABINETE|[A-Z.]+))?|ACUERDO(?:[ \t]+MUNICIPAL)?|FALLO)[ \t]+N[O.°º*?'’"“”:\s-]*((?:[A-Z][A-Z.]*[- \t]+)*(?:[\d]+(?:[-/][A-Z\d]+)*|S\/N))\b/gm,
				),
			]
			if (
				new Set(acts.map(match => `${match[1]} ${match[2]}`.replace(/\s+/g, ' '))).size > 1 ||
				(acts.length > 0 && /^[ \t]*(?:AVISOS|EDICTOS)\b/m.test(inventory))
			)
				return 'official_gazette_issue'
		}
		return record.textScope
	}
	if (record.sourceType !== 'infojuridica') return record.textScope
	const entries = Object.entries(record.documentUrls ?? {})
	const sourceFiles = entries.filter(([key]) => /^sourcePdf/i.test(key)).map(([, url]) => url)
	const usedFiles = sourceFiles.length
		? sourceFiles
		: entries.filter(([key]) => /^gazettePdf/i.test(key)).map(([, url]) => url)
	if (
		usedFiles.some(url => {
			try {
				return /\/(?:\d{4,6})(?:[-_][A-Z])?_\d{4}\.pdf$/i.test(new URL(url).pathname)
			} catch {
				return false
			}
		})
	)
		return 'official_gazette_issues'
	return record.textScope
}

function serializeLegalMetadata(value: unknown, maxLength: number) {
	if (value === undefined || value === null) return ''
	const serialized = typeof value === 'string' ? value : JSON.stringify(value)
	return serialized.length <= maxLength ? serialized : `${serialized.slice(0, maxLength)}…`
}

export function buildPanamaEmbeddingText(record: PanamaRecord, content: string) {
	const legalMetadata = record.legalMetadata
	const caseMetadata = record.caseMetadata
	return [
		'País: Panamá',
		`Fuente: ${record.sourceType}`,
		`Tipo: ${record.documentType}`,
		`Número: ${record.number}`,
		`Título: ${record.title}`,
		record.authority?.trim() ? `Autoridad registrada: ${record.authority.trim()}` : '',
		record.territorialScope?.locality ? `Ámbito territorial del catálogo: ${record.territorialScope.locality}` : '',
		record.catalogMetadata?.status ? `Anotación del catálogo oficial: ${record.catalogMetadata.status}` : '',
		// CENDOJ catalog dates describe uploads. Older indexed records also copied
		// that date into `date`; never promote it to a statute's issuance date.
		record.sourceType === 'cendoj-documents'
			? `Fecha de publicación en CENDOJ (no es la fecha de expedición de la norma): ${record.publishedAt || record.date || 'no disponible'}`
			: record.date
				? `Fecha del documento: ${record.date}`
				: `Fecha de publicación registrada (no acredita fecha de expedición): ${record.publishedAt || 'no disponible'}`,
		record.year ? `Año de referencia del catálogo: ${record.year}` : '',
		record.gazetteNumber ? `Gaceta: ${record.gazetteNumber}` : '',
		record.projectNumber ? `Proyecto de ley: ${record.projectNumber}` : '',
		record.anteprojectNumber ? `Anteproyecto de ley: ${record.anteprojectNumber}` : '',
		record.proceduralStage ? `Etapa legislativa actual: ${record.proceduralStage}` : '',
		record.proponents ? `Proponentes: ${record.proponents}` : '',
		record.textScope ? `Alcance del texto: ${record.textScope}` : '',
		legalMetadata?.vigency ? `Vigencia declarada por Infojurídica: ${legalMetadata.vigency}` : '',
		legalMetadata?.gazettes ? `Gacetas relacionadas: ${legalMetadata.gazettes}` : '',
		legalMetadata?.comment ? `Comentario oficial: ${serializeLegalMetadata(legalMetadata.comment, 4_000)}` : '',
		legalMetadata?.matters?.length ? `Materias: ${serializeLegalMetadata(legalMetadata.matters, 2_000)}` : '',
		legalMetadata?.vigencyHistory?.length
			? `Historial de vigencia: ${serializeLegalMetadata(legalMetadata.vigencyHistory, 4_000)}`
			: '',
		legalMetadata?.constitutionalCaseLaw?.length
			? `Jurisprudencia constitucional: ${serializeLegalMetadata(legalMetadata.constitutionalCaseLaw, 4_000)}`
			: '',
		legalMetadata?.references?.length
			? `Referencias y afectaciones: ${serializeLegalMetadata(legalMetadata.references, 6_000)}`
			: '',
		caseMetadata ? `Metadatos oficiales del caso: ${serializeLegalMetadata(caseMetadata, 8_000)}` : '',
		content,
	]
		.filter(Boolean)
		.join('\n')
}

export function buildPanamaCitationGroundingText(record: PanamaRecord, content: string) {
	// Catalogue cards are evidence for identity, publication, declared vigency and
	// recorded legal relationships. Persist those exact fields with the citation so
	// a reviewer can verify temporal claims; the short display text alone often
	// contains only the title. This still labels the scope as metadata and never
	// substitutes the card for omitted statutory articles.
	return record.textScope === 'official_catalog_metadata' ? buildPanamaEmbeddingText(record, content) : content
}
